<script>
  import Intersection from './intersection.svelte'
  import Svg from './svg/svg.svelte'
  import Marker from './svg/marker.svelte'
  import Global from './kartutsnitt/global.svelte'
  import Regional from './kartutsnitt/regional.svelte'
  import Lokal from './kartutsnitt/lokal.svelte'
  import { onMount } from 'svelte'
  import { calcMinHeight } from './view-box-calc.ts'
  import { params as lokalParams } from './kartutsnitt/lokal-params.ts'
  import { params as regionalParams } from './kartutsnitt/regional-params.ts'
  import { params as globalParams } from './kartutsnitt/global-params.ts'
  import TranslateGlobalMarkers from './svg/translate-global-markers.svelte'
  import {
    setupHeadingAnimation,
    setupKartAnimation,
    setupTimeline,
  } from './scroll-driven-animations.ts'

  export let tittel
  export let stikktittel
  export let utsnitt
  export let beskrivelse

  let kartEl
  let headingEl
  let isSticky
  let innerWidth = 428
  let viewTimeline

  // Distance between the bottom of the compressed sticky element and the end of the section (inside padding)
  // when it should be pushed out of the screen
  const stickyEnd = 'min(80vh,1000px)'

  // Min height must be expressed as px, as calculations in scroll driven animations are a bit limited
  $: minHeight = `${calcMinHeight(innerWidth).toFixed(2)}px`

  // Height of map is 929px,
  // should never be taller than the screen
  // and is scaled with a fixed aspect ratio on smaller mobiles
  $: maxHeight = `min(100vh, 929px, calc(100vw / 428 * 929))`

  // How much we need to translate the map towards the top:
  $: translateTarget = `calc(var(--min-height) - ${maxHeight})`

  function handleStickyLimitations(kart) {
    // Sticky elements have a special behavior in ViewTimelines.
    // Create another element with the same size as a basis for our timeline:
    const subject = document.createElement('div')
    subject.className = 'dh-sa-part-large'
    subject.style.height = maxHeight
    // Negate height so that the position of the map element is unaffected.
    subject.style.marginTop = '0'
    subject.style.marginBottom = `calc(-1 * ${maxHeight})`
    kart.before(subject)

    // Margin bottom on a sticky element have dual purpose. It is both a margin to the next sibling element
    // _and_ determines the sticky constraint rectangle (https://github.com/w3c/csswg-drafts/issues/2496)
    // This can be mitigated by using the bottom margin for the sticky constraing and
    // moving the difference to the next elements top margin:
    const nextElementSibling = kart.nextElementSibling
    if (nextElementSibling) {
      // Determine sticky end constraint
      kart.style.marginBottom = `calc(${minHeight} + ${stickyEnd} - ${maxHeight})`

      // Reset margin-bottom to next sibling element
      nextElementSibling.style.marginTop = `calc(${maxHeight} - ${minHeight} - ${stickyEnd} + var(--_rs) * 6)`
    }
    return subject
  }

  onMount(() => {
    const kart = kartEl.closest('.kart-sticky')
    kart.classList.add('aktiv')
    kart.style.pointerEvents = 'none'
    const subject = handleStickyLimitations(kart)
    viewTimeline = setupTimeline(subject)
    setupKartAnimation(viewTimeline, kartEl, (s) => {
      isSticky = s
    })
    setupHeadingAnimation(viewTimeline, headingEl)
  })

  $: params = getParams(utsnitt)
  $: isDesktop = innerWidth > 720

  function getParams(region) {
    switch (region) {
      case 'lokal':
        return lokalParams
      case 'regional':
        return regionalParams
      case 'global':
        return globalParams
    }
  }
</script>

<svelte:window bind:innerWidth />

<div
  id={utsnitt}
  class="wrapper"
  style={`--height: ${maxHeight}; --min-height: ${minHeight}; --translate-target: ${translateTarget};`}
>
  <div class="kart" bind:this={kartEl}>
    <div class="title-wrapper">
      <h2 bind:this={headingEl}>
        <span class="stikktittel">{stikktittel}</span>
        {tittel}
      </h2>
    </div>

    <Intersection let:activeRegion let:visibleRegions>
      <Svg
        {isSticky}
        {visibleRegions}
        {activeRegion}
        {viewTimeline}
        {params}
        {isDesktop}
        {beskrivelse}
        let:markers
        let:svgVisible
      >
        {#if utsnitt === 'lokal'}
          <Lokal />
        {:else if utsnitt === 'regional'}
          <Regional />
        {:else if utsnitt === 'global'}
          <Global />
        {/if}

        <TranslateGlobalMarkers {utsnitt} {isDesktop}>
          {#each markers as { text, id, ...marker }}
            <Marker
              {...marker}
              fill="#FEB937"
              {isSticky}
              {svgVisible}
              visible={!isSticky || id === 'gaza' || visibleRegions?.includes(id)}
              active={!isSticky || activeRegion === id}
            >
              {text}
            </Marker>
          {/each}
        </TranslateGlobalMarkers>
      </Svg>
    </Intersection>
  </div>
</div>

<style>
  :global(.kart-sticky.aktiv) {
    pointer-events: none;
    position: sticky;
    top: 0;
    margin-top: 0; /* Top of section */
    z-index: 10;
  }
  .kart,
  .wrapper {
    height: var(--height);
  }

  .wrapper {
    contain: strict;
    overflow: hidden;
    overflow: clip;
  }

  .kart {
    contain: strict;
    pointer-events: auto;
    background: var(--dhsa-color-background-primary);
    box-sizing: border-box;
    border-top: 1px solid #49668a;
    border-bottom: 1px solid #49668a;
    overflow: hidden;
    overflow: clip;
    position: relative;

    /* Grid from Stilbar Artikkel */
    display: grid;
    grid-template-columns:
      [large-start medium-start] var(--_mobile-padding) [text-start small-start] minmax(auto, 1fr)
      [small-end] var(--_text-overflow) [text-end] calc(
        var(--_mobile-padding) - var(--_text-overflow)
      )
      [medium-end large-end];
  }

  @media (width >= 720px) {
    .kart {
      grid-template-columns:
        [large-start] 1.618fr [medium-start] 1fr [text-start small-start] minmax(auto, 600px)
        [small-end text-end] 1fr [medium-end] 1.618fr [large-end];
    }
  }

  .kart :global(svg) {
    fill: none;
  }

  .title-wrapper {
    position: relative;
    grid-column: small-start/small-end;
    grid-row: 1/1;
    z-index: 10;
  }

  h2 {
    position: absolute;
    bottom: 5%;
    left: 12px;
    font-size: fluid(2.2rem, 2.5rem);
    min-width: 0; /* Must be set to prevent min-width auto */
    will-change: opacity;
    text-wrap: balance;
  }

  @media (width >= 720px) {
    h2 {
      left: 0;
    }
  }

  .stikktittel {
    display: block;
    font-size: calc(14 / 16 * 1rem);
    font-weight: var(--nrk-sans-weight-medium);
    text-transform: uppercase;
    color: var(--dhsa-color-accent-primary);
  }
</style>
